import {
  mapInstallmentsSelectInputData,
  getSelectedInstallment,
} from '@openeducation/pp-js-utils';
import { selectedInstallmentAtom } from '@openeducation/pp-rn-jotai-atoms';
import type {
  CreditCard,
  Currency,
  Organization,
  Installment,
} from '@openeducation/pp-rn-shared-types';
import { useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetInstallments } from '../../../hooks';

interface UseInstallments {
  installmentsFormData: string;
  handleFieldChange: (value: string, name?: string) => void;
  fieldName: string;
  cardType?: CreditCard;
  amount?: number;
  billingFrequency?: string;
  currency?: Currency;
  initialTerm?: number;
  organization?: Organization;
  cardBinNumber: string;
  splitInvoice?: boolean;
  isB2B?: boolean;
}

export const useInstallmentsInput = ({
  installmentsFormData,
  cardType,
  handleFieldChange,
  fieldName,
  amount,
  billingFrequency,
  currency,
  initialTerm,
  organization,
  cardBinNumber,
  splitInvoice,
  isB2B,
}: UseInstallments) => {
  const setSelectedInstallmentAtom = useSetAtom(selectedInstallmentAtom);
  const { t } = useTranslation();
  const { data: installmentsData = [] as Installment[] } = useGetInstallments({
    cardType,
    amount,
    billingFrequency,
    currency,
    initialTerm,
    organization,
    cardBinNumber,
    splitInvoice,
    isB2B,
  });
  const installmentsInputData = useMemo(() => {
    if (!currency || installmentsData.length === 0) {
      return [];
    }
    return mapInstallmentsSelectInputData(
      installmentsData,
      currency,
      t('ppui.installments.total')
    );
  }, [installmentsData, currency, t]);

  const selectedInstallment = useMemo(
    () => getSelectedInstallment(installmentsData, installmentsFormData),
    [installmentsData, installmentsFormData]
  );

  useEffect(() => {
    if (selectedInstallment) {
      setSelectedInstallmentAtom(selectedInstallment);
    }
  }, [selectedInstallment, setSelectedInstallmentAtom]);

  useEffect(() => {
    if (installmentsFormData !== '' && installmentsInputData?.length === 0) {
      handleFieldChange('', fieldName);
    }
  }, [
    installmentsInputData,
    handleFieldChange,
    installmentsFormData,
    fieldName,
  ]);
  return {
    installmentsInputData,
  };
};
